import { Formik } from 'formik';
import Fuse from 'fuse.js';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as Yup from 'yup';
import {
  Avatar,
  Button,
  Buttons,
  CancelButton,
  Confirmation,
  Drawer,
  Field,
  Form,
  MemberContactPopover,
  MultiSelect,
  Radio,
} from '~/components';
import { useApi, useConfirmation, useToast, useWorkspace } from '~/contexts';
import { useAuth, useDirtyCheck, useFeatures } from '~/hooks';
import { emptyStringToNull, mergeValues } from '~/utils';

export default function ProjectRoleForm({ projectId, projectRoleId, onSaved, onCancel }) {
  const features = useFeatures();
  const auth = useAuth();
  const { workspace } = useWorkspace();
  const api = useApi();
  const toast = useToast();

  const [project, setProject] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.www.workspaces(workspace.id).projects(projectId).get();
        setProject(data);
      } catch {
        // Do nothing
      }
    })();
  }, [api, workspace.id, projectId]);

  const [projectRole, setProjectRole] = useState(null);
  useEffect(() => {
    (async () => {
      if (!projectRoleId) {
        setProjectRole({});
        return;
      }

      try {
        const { data } = await api.www
          .workspaces(workspace.id)
          .projectSettings(projectId)
          .projectRoles(projectRoleId)
          .get();
        setProjectRole(data);
      } catch {
        // Do nothing
      }
    })();
  }, [api, workspace.id, projectId, projectRoleId]);

  const formRef = useRef();
  const dirtyCheck = useDirtyCheck(() => formRef.current.dirty);
  const confirmation = useConfirmation();

  if (!project || !projectRole) return null;

  const initialValues = mergeValues(
    {
      discipline: null,
      practice: null,
      location: null,
      isBillable: project.isBillable,
      isActive: true,
      name: '',
      rate: '',
      members: [],
    },
    {
      ...projectRole,
      members: projectRole?.members?.map((pmr) => pmr.projectMember) ?? [],
    },
  );

  const handleSubmit = async (values, formik) => {
    values = emptyStringToNull({
      ..._.omit(values, ['discipline', 'practice', 'location']),
      disciplineId: values.discipline?.id ?? null,
      practiceId: values.practice?.id ?? null,
      locationId: values.location?.id ?? null,
      members: values.members.map((m) => ({ projectMemberId: m.id })),
    });

    if (
      projectRole.hasTimeEntries &&
      (projectRole.isBillable !== values.isBillable || projectRole.rate !== values.rate)
    ) {
      const confirm = await confirmation.prompt((resolve) => (
        <Confirmation resolve={resolve}>
          You have changed the bill rate and this role already has existing time entries. If you want those existing
          time entries to use the new rate, click OK below. If you do not want the rate to change on the existing time
          entries, click Cancel.
        </Confirmation>
      ));

      if (!confirm) return;
    }

    formik.setSubmitting(true);

    try {
      if (projectRoleId) {
        await api.www.workspaces(workspace.id).projectSettings(projectId).projectRoles(projectRoleId).patch(values);
      } else {
        await api.www.workspaces(workspace.id).projectSettings(projectId).projectRoles().post(values);
      }

      toast.success('Project Role saved successfully.');
      await onSaved();
    } catch (error) {
      toast.error(error.message);
    }

    formik.setSubmitting(false);
  };

  return (
    <Drawer
      title={projectRoleId ? 'Edit Project Role' : 'Add Project Role'}
      isOpen
      onBeforeClose={({ setIsOpen }) => dirtyCheck(() => setIsOpen(false))}
      onClose={onCancel}>
      {(closeDrawer) => {
        const handleCloseClick = () => dirtyCheck(() => closeDrawer());

        return (
          <Formik
            innerRef={formRef}
            enableReinitialize
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validateOnBlur={false}
            validateOnChange={false}
            validationSchema={Yup.object().shape({
              isBillable: Yup.bool().label('Is Billable'),
              name: Yup.string().label('Name').max(255).required(),
              rate: Yup.number().label('Rate').min(0).max(99999999999).nullable(),
            })}>
            {(formik) => {
              return (
                <Form>
                  <Form.Section title="Details">
                    <Form.Control>
                      <Field.Text autoFocus name="name" placeholder="Name" maxLength={255} />
                    </Form.Control>

                    {features.practices && (
                      <Form.Control>
                        <Field.PracticeSelect name="practice" placeholder="Practice" />
                      </Form.Control>
                    )}

                    <Form.Control>
                      <Field.LocationSelect name="location" placeholder="Location" />
                    </Form.Control>

                    {features.disciplines && (
                      <Form.Control>
                        <Field.DisciplineSelect name="discipline" placeholder="Discipline" />
                      </Form.Control>
                    )}
                  </Form.Section>

                  <Form.Section title="Billing">
                    {project.isBillable && (
                      <Form.Control>
                        <Field.RadioGroup name="isBillable">
                          <Radio value={true} label="Billable" disabled={!auth.projects.sections.billing} />
                          <Radio value={false} label="Non-Billable" disabled={!auth.projects.sections.billing} />
                        </Field.RadioGroup>
                      </Form.Control>
                    )}

                    {formik.values.isBillable && (
                      <Form.Control>
                        <Field.Currency
                          name="rate"
                          placeholder="Rate"
                          precision={7}
                          currency={project.currency}
                          disabled={!auth.projects.sections.billing}
                        />
                      </Form.Control>
                    )}
                  </Form.Section>

                  <Form.Section title="Member Assignments">
                    <Form.Control style={{ fontSize: '.875rem' }}>
                      <ProjectMemberSelect
                        projectId={projectId}
                        value={formik.values.members}
                        onChange={({ value }) => formik.setFieldValue('members', _.orderBy(value, 'member.name'))}
                      />
                    </Form.Control>
                  </Form.Section>

                  <Drawer.Actions>
                    <Buttons align="right">
                      <CancelButton onClick={handleCloseClick}>Close</CancelButton>

                      <Button isLoading={formik.isSubmitting} onClick={formik.submitForm}>
                        Save &amp; Close
                      </Button>
                    </Buttons>
                  </Drawer.Actions>
                </Form>
              );
            }}
          </Formik>
        );
      }}
    </Drawer>
  );
}

function ProjectMemberSelect({ projectId, value, onChange }) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const [data, setData] = useState(null);
  const [options, setOptions] = useState([]);

  const handleSearch = useCallback(
    async (q) => {
      let options = data;
      if (!data) {
        const result = await api.www.workspaces(workspace.id).projectSettings(projectId).members().assignable();
        setData(result.data);
        options = result.data;
      }
      if (q) {
        options = new Fuse(options, { keys: ['member.name'], threshold: 0.4 }).search(q).map((result) => result.item);
      }
      setOptions(options);
    },
    [workspace.id, api, data, projectId],
  );

  const groups = useMemo(() => {
    if (!options) return null;
    return _(options)
      .orderBy((o) => (value.some((v) => v.id === o.id) ? 0 : o.isActuallyActive ? 1 : 2))
      .groupBy((o) => (value.some((v) => v.id === o.id) ? 'Selected' : o.isActuallyActive ? 'Active' : 'Inactive'))
      .value();
  }, [options, value]);

  return (
    <MultiSelect.Input
      materialAlwaysVisible={false}
      materialPlaceholder={_.isEmpty(value) ? null : 'Member Assignments'}
      placeholder="Member Assignments"
      value={value}
      renderValue={(value) => value.map((v) => v.member.name).join('; ')}
      options={options}
      renderOptions={(opts) => {
        const optionsById = _.keyBy(opts, 'id');
        return Object.keys(groups).map((status) => (
          <MultiSelect.Group key={status} label={status}>
            {groups[status].map((option) => (
              <MultiSelect.Option
                key={option.id}
                inactive={!option.isActuallyActive}
                style={{ display: 'flex' }}
                {...optionsById[option.id].props}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <MemberContactPopover member={option.member} placement="left">
                    <Avatar value={option.member} isCircle hasBackground initialsFontSize=".7rem" size="1.5rem" />
                  </MemberContactPopover>
                  <span style={{ marginLeft: '.5rem' }}>{option.member.name}</span>
                </div>
              </MultiSelect.Option>
            ))}
          </MultiSelect.Group>
        ));
      }}
      isLoading={!data}
      onSearch={handleSearch}
      onChange={onChange}
    />
  );
}
